import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { logError, logInfo } from './logger';

export const generateShareLink = async (surveyId, options) => {
  try {
    if (!surveyId) {
      throw new Error('Survey ID is required');
    }

    // Generate unique share ID
    const shareId = `${surveyId}-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    
    // Create share configuration in Firestore
    const shareRef = doc(db, 'shared_maps', shareId);
    await setDoc(shareRef, {
      surveyId,
      type: options?.type || 'public',
      enableEditing: options?.enableEditing || false,
      selectedUsers: options?.type === 'selected' ? options.selectedUsers : [],
      customizations: options?.customizations ? options.customizations : {
        logo: { type: 'default' },
        mapType: 'roadmap',
        interface: {
          showSearch: true,
          showInfoBox: true,
          showMenu: true,
          showLocationButton: true,
          showDriveToButton: true
        },
        display: {
          showSurveyName: true,
          showCustomerName: true,
          showDates: true,
          showVehicleDetails: true,
          showAdditionalDetails: true
        },
        sidebar: {
          enabled: true,
          tabs: {
            basic: true,
            routes: true,
            pois: true,
            share: true,
            files: true,
            settings: true
          }
        }
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    const shareLink = `${window.location.origin}/shared/${shareId}`;
    logInfo('Share link generated successfully');
    return shareLink;
  } catch (error) {
    logError('Failed to generate share link:', error);
    throw new Error('Failed to generate share link');
  }
};

export const validateShareAccess = async (shareId) => {
  try {
    if (!shareId) {
      return {
        hasAccess: false,
        error: 'Invalid share link',
        shareData: null 
      };
    }

    // Get share data
    const shareRef = doc(db, 'shared_maps', shareId);
    const shareDoc = await getDoc(shareRef);
    
    if (!shareDoc.exists()) {
      return {
        hasAccess: false,
        error: 'Share link not found',
        shareData: null
      };
    }

    const shareData = shareDoc.data();

    // Validate access based on share type
    switch (shareData.type) {
      case 'public':
      case 'secret':
        // Public and secret links are accessible to anyone
        return { hasAccess: true, error: null, shareData };

      case 'selected':
        // For public sharing, we'll allow access to selected user links
        // without authentication for backward compatibility
        return { hasAccess: true, error: null, shareData };

      default:
        return {
          hasAccess: false,
          error: 'Invalid share type',
          shareData: null
        };
    }
  } catch (error) {
    logError('Share access validation failed:', error);
    return {
      hasAccess: false,
      error: 'Failed to validate share access',
      shareData: null
    };
  }
};